"use client";

import { cn } from "@/lib/utils";
import { memo, useLayoutEffect, useRef } from "react";

interface RichTextEditor1Props {
  /** When true, strip outer centering/max-width so blocks stack in the blog left column. */
  blogColumn?: boolean;
  data?: {
    data?: {
      content?: string;
      background_color?: string;
      status?: boolean | number | string;
      enable_padding?: boolean | number | string;
      padding_x?: number | string;
      padding_y?: number | string;
    };
  };
}

function RichTextEditor1({
  data,
  blogColumn = false,
}: RichTextEditor1Props) {
  // Extract nested data property (matching other components like Banner1, Header1)
  // Handle case where data might be undefined or already the section data
  const sectionData = data?.data || (data as any);

  if (!sectionData) {
    return null;
  }

  // Handle both boolean and numeric status (MySQL stores booleans as 0/1)
  const isActive =
    sectionData?.status === true ||
    sectionData?.status === 1 ||
    sectionData?.status === "1";

  if (!isActive) {
    return null;
  }

  // Check if padding is enabled from backend
  const enablePadding =
    sectionData?.enable_padding === true ||
    sectionData?.enable_padding === 1 ||
    sectionData?.enable_padding === "1";

  // Optional configurable padding (px). Falls back to the default Tailwind classes when blank.
  const toPx = (v?: number | string) => {
    if (v === undefined || v === null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? `${n}px` : undefined;
  };
  // Honor configured padding in every layout mode (incl. blog column); inline styles override the default classes.
  const paddingX = enablePadding ? toPx(sectionData?.padding_x) : undefined;
  const paddingY = enablePadding ? toPx(sectionData?.padding_y) : undefined;

  return (
    <section
      className={cn(
        "w-full",
        !blogColumn && "flex justify-center items-center",
        // Mobile (< 640px) gets a fixed 16px gutter; defaults and configured values apply from 640px up.
        !blogColumn && "max-[639px]:px-[16px] max-[639px]:py-[16px]",
        !blogColumn && paddingX === undefined && "sm:px-9",
        blogColumn && "px-0 sm:py-0",
        enablePadding && paddingY === undefined && "sm:py-10",
        paddingX !== undefined && "rte-pad-x",
        paddingY !== undefined && "rte-pad-y"
      )}
      style={
        {
          backgroundColor: blogColumn
            ? "transparent"
            : sectionData?.background_color || "#FFFFFF",
          ...(paddingX !== undefined ? { "--rte-px": paddingX } : {}),
          ...(paddingY !== undefined ? { "--rte-py": paddingY } : {}),
        } as React.CSSProperties
      }
    >
      <div
        className={cn(
          "flex flex-col w-full",
          !blogColumn &&
            "max-w-full sm:max-w-[1080px] md:max-w-[1230px] lg:max-w-[1430px] xl:max-w-[1750px] mx-auto"
        )}
      >
        {sectionData?.content && (
          <div
            className={cn(
              "rich-text-content prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-primary-main prose-strong:text-gray-900"
            )}
            dangerouslySetInnerHTML={{ __html: sectionData.content }}
          />
        )}
      </div>
    </section>
  );
}

export default memo(RichTextEditor1);
